/**
 * aC Men's Hub の課題管理（声 → 論点 → 課題 → 案件の台帳）の語彙と API 契約（zod schema + 型）。
 *
 * 課題・声・論点・テーマ・課題と案件の紐づけの読み書きを、API（apps/api/ac-mens-hub）と
 * Web（apps/web/ac-mens-hub）で同じ型から扱う。判定・進捗・見せる状態の対応表もここを唯一の定義にする。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の台帳の語彙（判定・振り分け・見せる状態）と、一覧・詳細・編集・判定・統合・紐づけ・元に戻すの API 契約を API と Web で共有する
 * @graph-connects none
 */
import { z } from 'zod';
import { isCalendarDate } from './calendar-date.js';
import { IssueNextActionSchema, IssueVerdictSchema, IssueWaitingSchema, } from './issue-mgmt-status.js';
/**
 * 課題管理を見る権限（課題タブ・設定タブ・声の本文と元投稿リンク）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の台帳を閲覧するために必要な権限を表す
 * @graph-connects none
 */
export const ISSUE_MGMT_PERMISSION = 'issue-mgmt';
/**
 * 課題管理を変える権限（編集・判定・統合・紐づけ・声を入れる・元に戻す）。
 *
 * 閲覧キーの配下に重ねて要求するので、書き込みには閲覧＋編集の両方が要る。
 * 担当の候補も、このキーを持つ人に限る。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の台帳を変更するために必要な権限を表す。閲覧とは独立に配り、担当の候補もこの権限の保持者に限る
 * @graph-connects none
 */
export const ISSUE_MGMT_WRITE_PERMISSION = 'issue-mgmt-write';
/**
 * 課題の番号（K-001。棚卸しの番号をそのまま使い、続き番号で増える）。
 * @graph-connects none
 */
export const IssueIdSchema = z.string().regex(/^K-\d{3,}$/);
/**
 * 声の番号（V-00001）。
 * @graph-connects none
 */
export const VoiceIdSchema = z.string().regex(/^V-\d{5,}$/);
/**
 * 論点の番号（声の番号＋枝番。V-00001-1）。
 * @graph-connects none
 */
export const PointIdSchema = z.string().regex(/^V-\d{5,}-\d+$/);
/**
 * Backlog の課題キー（AIRCLOSET-123）。メンズ以外のプロジェクトのキーも受け付ける。
 * 前後の空白を除き大文字にそろえてから形を見る。
 * @graph-connects none
 */
export const CaseKeySchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z][A-Z0-9_]*-\d+$/);
/**
 * Firestore のドキュメント ID に使う値（テーマ・保存操作）。英数・ハイフン・アンダースコアだけを受ける
 * （`/` が通るとコレクションのパスとして読まれ、400 ではなく 500 になるため）。
 * @graph-connects none
 */
const DocumentIdSchema = z.string().regex(/^[A-Za-z0-9_-]{1,64}$/);
/**
 * テーマの ID。
 * @graph-connects none
 */
export const ThemeIdSchema = DocumentIdSchema;
/**
 * 判定の操作で選べる判定（未判定へは戻さず、統合済みは統合の操作で付ける）。
 * @graph-connects none
 */
export const IssueDecisionSchema = z.enum(['accepted', 'on_hold', 'rejected']);
/**
 * 課題の優先度。
 * @graph-connects none
 */
export const IssuePrioritySchema = z.enum(['high', 'medium', 'low']);
/**
 * 「対応しない」の理由。仕様どおり／効果に比べ対応が重い／再現しない・情報不足／方針として行わない／
 * 他の手段で解消済み／その他（補足が要る）。
 * @graph-connects none
 */
export const IssueRejectReasonSchema = z.enum([
    'as_designed',
    'cost_outweighs',
    'not_reproducible',
    'policy',
    'resolved_elsewhere',
    'other',
]);
/**
 * 開発しない課題の進捗（人が進める）。開発が要る課題の進捗は Backlog の案件から出す。
 * @graph-connects none
 */
export const IssueManualProgressSchema = z.enum(['not_started', 'in_progress', 'done']);
/**
 * 中身3項目の印。AI が書いたままなら ai_draft、人が書いた・確かめたなら human。
 * @graph-connects none
 */
export const IssueContentMarkSchema = z.enum(['ai_draft', 'human']);
/**
 * 工数の目安（開発計画の「規模」の受け皿。声の数とは別物）。
 * @graph-connects none
 */
export const IssueEffortEstimateSchema = z.enum(['small', 'medium', 'large']);
/**
 * 論点の振り分け状態。
 * @graph-connects none
 */
export const PointStatusSchema = z.enum([
    'unassigned',
    'linked',
    'not_issue',
    'later',
    'legacy_done',
]);
/**
 * 声の由来。
 * @graph-connects none
 */
export const VoiceOriginSchema = z.enum(['slack', 'manual', 'legacy']);
/**
 * 手で入れた声の発生元。
 * @graph-connects none
 */
export const ManualVoiceSourceSchema = z.enum(['phone', 'meeting', 'chat', 'other']);
/**
 * 課題に紐づいていない声の状態（検索結果の下段に出す区分）。
 * excluded は AI が論点なしとした投稿、unassigned・later は振り分けを待っている声。
 * @graph-connects none
 */
export const UnlinkedVoiceStateSchema = z.enum([
    'legacy_done',
    'not_issue',
    'excluded',
    'unassigned',
    'later',
]);
/**
 * テーマの状態。終了したテーマは新しく付けられない。
 * @graph-connects none
 */
export const ThemeStatusSchema = z.enum(['active', 'ended']);
/**
 * 課題と案件の紐づけの状態。pending はミラーでまだ見つかっていない（確認待ち）。
 * @graph-connects none
 */
export const CaseLinkStateSchema = z.enum(['pending', 'confirmed', 'needs_check']);
/**
 * 画面に見せる課題の状態（5つ）。統合済みは一覧に出さないので含まない。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 画面に見せる課題の状態（判定待ち・対応中・保留・解決済み・対応しない）
 * @graph-connects none
 */
export const IssueDisplayStatusSchema = z.enum([
    'awaiting_decision',
    'in_progress',
    'on_hold',
    'resolved',
    'rejected',
]);
/**
 * 一覧で状態を並べる順（判定待ち → 対応中 → 保留 → 解決済み → 対応しない）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題の一覧で、見せる状態を並べる順を定める
 * @graph-connects none
 */
export const ISSUE_DISPLAY_STATUS_ORDER = [
    'awaiting_decision',
    'in_progress',
    'on_hold',
    'resolved',
    'rejected',
];
/**
 * 判定と印から、画面に見せる状態を1つ決める。統合済みは見せないので null。
 *
 * 対応表: 未判定・再浮上中 → 判定待ち／対応する × 解決確認済み → 解決済み／対応する × それ以外 → 対応中／
 * 保留 → 保留／対応しない → 対応しない。進捗の細かい段（起案済み・対応完了など）は状態に出さない。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題の判定・解決確認・再浮上から、画面に見せる5つの状態のどれかを決める
 * @graph-connects none
 */
export function deriveIssueDisplayStatus(input) {
    if (input.verdict === 'merged')
        return null;
    if (input.verdict === 'pending' || input.resurfaced)
        return 'awaiting_decision';
    switch (input.verdict) {
        case 'accepted':
            return input.resolvedAt === null ? 'in_progress' : 'resolved';
        case 'on_hold':
            return 'on_hold';
        case 'rejected':
            return 'rejected';
        default: {
            const unreachable = input.verdict;
            throw new Error(`unknown verdict: ${String(unreachable)}`);
        }
    }
}
/**
 * 課題のタイトルの上限字数。
 * @graph-connects none
 */
export const ISSUE_TITLE_MAX_LENGTH = 80;
/**
 * 中身3項目・メモ・声の本文などの文の上限字数。
 * @graph-connects none
 */
export const ISSUE_TEXT_MAX_LENGTH = 2000;
/**
 * 「対応しない: その他」の補足の最小字数。
 * @graph-connects none
 */
export const REJECT_OTHER_NOTE_MIN_LENGTH = 10;
/**
 * 保留の見直す日に選べる最も遠い日（今日から何日後まで）。
 * @graph-connects none
 */
export const HOLD_REVIEW_MAX_DAYS = 180;
/**
 * 一覧で既定で隠す、解決確認からの月数。
 * @graph-connects none
 */
export const RESOLVED_HIDE_AFTER_MONTHS = 6;
/** 課題のタイトル。 @graph-connects none */
const IssueTitleSchema = z.string().trim().min(1).max(ISSUE_TITLE_MAX_LENGTH);
/** 中身3項目などの文。空は null で表す。 @graph-connects none */
const IssueTextSchema = z.string().trim().min(1).max(ISSUE_TEXT_MAX_LENGTH).nullable();
/** 暦の日付（YYYY-MM-DD）。 @graph-connects none */
const DateTextSchema = z
    .string()
    .refine(isCalendarDate, { message: '暦にある日付（YYYY-MM-DD）を指定してください' });
/** 年月（YYYY-MM）。 @graph-connects none */
const MonthTextSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);
/**
 * 領域（棚卸しの区分名をそのまま選択肢にする）。区分名は移行の下見で棚卸しから読むため、ここでは字数だけを見る。
 * @graph-connects none
 */
export const IssueDomainSchema = z.string().trim().min(1).max(40);
/**
 * 中身3項目の1つ。
 * @graph-connects none
 */
export const IssueContentFieldSchema = z.object({
    text: z.string().nullable(),
    mark: IssueContentMarkSchema.nullable(),
});
/**
 * 課題の担当（メールと表示名）。hasEditKey は今も編集キーを持っているか（外されたら false）。
 * @graph-connects none
 */
export const IssueAssigneeSchema = z.object({
    email: z.string(),
    displayName: z.string(),
    hasEditKey: z.boolean(),
});
/**
 * 一覧の行に出す、検索に当たった声の1行。
 * @graph-connects none
 */
export const IssueMatchedVoiceSchema = z.object({
    voiceId: z.string(),
    text: z.string(),
});
/**
 * 課題の一覧の1行。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題の一覧の1行（番号・タイトル・見せる状態・優先度・担当・声の数・今の案内・検索に当たった声・次にやることと待ちの様子）
 * @graph-connects none
 */
export const IssueSummarySchema = z.object({
    id: z.string(),
    title: z.string(),
    displayStatus: IssueDisplayStatusSchema,
    verdict: IssueVerdictSchema,
    priority: IssuePrioritySchema.nullable(),
    assignee: IssueAssigneeSchema.nullable(),
    voiceCount: z.number().int(),
    recentVoiceCount: z.number().int(),
    guidance: z.string().nullable(),
    themeIds: z.array(z.string()),
    domain: z.string().nullable(),
    matchedVoice: IssueMatchedVoiceSchema.nullable(),
    mergedFromMatched: z.array(z.string()),
    next: IssueNextActionSchema.nullable(),
    waiting: IssueWaitingSchema.nullable(),
});
/**
 * 課題に紐づいていない声の1行（検索したときだけ表の下に出す）。
 * @graph-connects none
 */
export const UnlinkedVoiceSchema = z.object({
    voiceId: z.string(),
    postedAt: z.string().nullable(),
    postedDate: z.string().nullable(),
    channelName: z.string().nullable(),
    isPrivate: z.boolean(),
    text: z.string(),
    state: UnlinkedVoiceStateSchema,
    slackUrl: z.string().nullable(),
});
/**
 * 検索結果の下段に出す「課題に紐づいていない声」を、一度に返せる最大の件数。
 * @graph-connects none
 */
export const ISSUE_UNLINKED_LIMIT_MAX = 200;
/**
 * 課題の一覧の条件。query は番号・旧番号・Backlog キー・タイトル・中身・テーマ名・領域・声の本文・
 * 声の番号・Slack の投稿 URL に当てる（空白で区切った語はすべて含むものに当たる）。
 * @graph-connects none
 */
export const IssueListQuerySchema = z.object({
    query: z.string().trim().max(200).optional(),
    themeId: ThemeIdSchema.optional(),
    includeHidden: z.boolean().optional(),
    unlinkedLimit: z.number().int().min(1).max(ISSUE_UNLINKED_LIMIT_MAX).optional(),
});
/**
 * 課題の一覧の応答。
 * - hiddenCount: 既定で隠した（統合済み・解決確認から6か月超）件数。includeHidden のときは 0
 * - unlinkedVoices: 検索したときだけ。total は当たった全件、items は先頭 unlinkedLimit 件（既定 20）
 * - slackUrlNotFound: 検索語が Slack の投稿 URL で、その投稿の声がまだ無い
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題の一覧・検索の応答（課題の行、隠した件数、課題に紐づいていない声）
 * @graph-connects none
 */
export const IssueListResponseSchema = z.object({
    issues: z.array(IssueSummarySchema),
    hiddenCount: z.number().int(),
    unlinkedVoices: z
        .object({ total: z.number().int(), items: z.array(UnlinkedVoiceSchema) })
        .nullable(),
    slackUrlNotFound: z.boolean(),
});
/**
 * 課題の詳細に出す声1件（論点の文と、その元の声）。
 * @graph-connects none
 */
export const IssueVoiceEntrySchema = z.object({
    pointId: z.string(),
    pointTitle: z.string(),
    pointBody: z.string().nullable(),
    repliedAt: z.string().nullable(),
    repliedBy: z.string().nullable(),
    voiceId: z.string(),
    origin: VoiceOriginSchema,
    postedAt: z.string().nullable(),
    postedDate: z.string().nullable(),
    channelName: z.string().nullable(),
    isPrivate: z.boolean(),
    slackUrl: z.string().nullable(),
    manualSource: ManualVoiceSourceSchema.nullable(),
    legacyIds: z.array(z.string()),
    body: z.string().nullable(),
    bodyDeleted: z.boolean(),
});
/**
 * 課題の詳細の応答。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題1件の詳細（見出し・中身3項目・声・案件の紐づけ・テーマ・統合・移行時の説明・対応の記録・再発の印・次にやることと待ちの様子と、競合検知に使う各項目の値）
 * @graph-connects none
 */
export const IssueDetailSchema = z.object({
    id: z.string(),
    title: z.string(),
    displayStatus: IssueDisplayStatusSchema.nullable(),
    verdict: IssueVerdictSchema,
    decidedAt: z.string().nullable(),
    decidedBy: z.string().nullable(),
    assignee: IssueAssigneeSchema.nullable(),
    priority: IssuePrioritySchema.nullable(),
    devRequired: z.boolean().nullable(),
    holdUntil: z.string().nullable(),
    rejectReason: IssueRejectReasonSchema.nullable(),
    verdictNote: z.string().nullable(),
    content: z.object({
        what: IssueContentFieldSchema,
        guidance: IssueContentFieldSchema,
        fixed: IssueContentFieldSchema,
    }),
    domain: z.string().nullable(),
    targetMonth: z.string().nullable(),
    effortEstimate: IssueEffortEstimateSchema.nullable(),
    relatedLinks: z.array(z.string()),
    memo: z.string().nullable(),
    legacyIds: z.array(z.string()),
    migrationNote: z.string().nullable(),
    manualProgress: IssueManualProgressSchema.nullable(),
    doneNote: z.string().nullable(),
    resolvedAt: z.string().nullable(),
    unresolvedAt: z.string().nullable(),
    themes: z.array(z.object({ id: z.string(), name: z.string(), status: ThemeStatusSchema })),
    caseLinks: z.array(z.object({ caseKey: z.string(), state: CaseLinkStateSchema })),
    mergedInto: z.object({ id: z.string(), title: z.string() }).nullable(),
    mergedFrom: z.array(z.object({ id: z.string(), title: z.string() })),
    voices: z.array(IssueVoiceEntrySchema),
    voiceCount: z.number().int(),
    recentVoiceCount: z.number().int(),
    next: IssueNextActionSchema.nullable(),
    waiting: IssueWaitingSchema.nullable(),
    previousVerdict: IssueVerdictSchema.nullable(),
    reviewedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    revision: z.number().int(),
});
/**
 * 課題の詳細を引く条件。
 * @graph-connects none
 */
export const IssueDetailQuerySchema = z.object({ issueId: IssueIdSchema });
/**
 * 変更履歴の1行。自由文の項目は値を残さないので before / after が null で valuesOmitted が true。
 * before / after は JSON 文字列（値の null は文字列の 'null'）。
 * @graph-connects none
 */
export const IssueChangeLogEntrySchema = z.object({
    changeId: z.string(),
    operationId: z.string(),
    changedAt: z.string(),
    actorType: z.enum(['user', 'job', 'migration']),
    actor: z.string(),
    entityType: z.enum(['issue', 'point', 'voice', 'case_link', 'theme', 'channel', 'inspection']),
    entityId: z.string(),
    issueId: z.string().nullable(),
    field: z.string(),
    before: z.string().nullable(),
    after: z.string().nullable(),
    valuesOmitted: z.boolean(),
    revertsOperationId: z.string().nullable(),
    changeOperation: z.enum(['create', 'update', 'delete', 'undo']),
});
/**
 * 課題の変更履歴を引く条件（新しい順に limit 件、offset 件目から）。
 * @graph-connects none
 */
export const IssueHistoryQuerySchema = z.object({
    issueId: IssueIdSchema,
    limit: z.number().int().min(1).max(100).optional(),
    offset: z.number().int().min(0).optional(),
});
/**
 * 課題の変更履歴の応答。
 * @graph-connects none
 */
export const IssueHistoryResponseSchema = z.object({ entries: z.array(IssueChangeLogEntrySchema) });
/**
 * 担当の候補（課題管理の編集キーを持つ人）。表示名が名簿で引けなければメールを出す。
 * @graph-connects none
 */
export const IssueMemberSchema = z.object({
    email: z.string(),
    displayName: z.string(),
    slackId: z.string().nullable(),
});
/**
 * 担当の候補の応答。
 * @graph-connects none
 */
export const IssueMembersResponseSchema = z.object({ members: z.array(IssueMemberSchema) });
/**
 * 領域の選択肢の応答（台帳の課題が使っている領域。名前の順）。
 * @graph-connects none
 */
export const IssueDomainsResponseSchema = z.object({ domains: z.array(z.string()) });
/**
 * テーマ1件。
 * @graph-connects none
 */
export const IssueThemeSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    status: ThemeStatusSchema,
});
/**
 * テーマの一覧の応答。
 * @graph-connects none
 */
export const IssueThemesResponseSchema = z.object({ themes: z.array(IssueThemeSchema) });
/**
 * 保存の結果。operationId は「元に戻す」に渡す。変わったものが無ければ（同じ判定の押し直しなど）何も記録せず null。
 * maskedFields は伏せ字を当てた項目（画面で「伏せ字にしました」を出す）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の保存操作の結果（元に戻す操作の ID と伏せ字を当てた項目）
 * @graph-connects none
 */
export const IssueMutationResultSchema = z.object({
    operationId: z.string().nullable(),
    maskedFields: z.array(z.string()),
});
/**
 * 作った課題・声を返す保存の結果。
 * @graph-connects none
 */
export const IssueCreatedResultSchema = IssueMutationResultSchema.extend({ id: z.string() });
/**
 * 競合した項目1つ（画面を開いた時点から、他の人が先に変えていた項目）。
 * currentValue は今の値、changedBy は最後に変えた人のメール（記録が無ければ null）。
 * @graph-connects none
 */
export const IssueFieldConflictSchema = z.object({
    field: z.string(),
    currentValue: z.unknown(),
    changedBy: z.string().nullable(),
    changedAt: z.string().nullable(),
});
/**
 * 競合のエラーに載せる中身。
 * @graph-connects none
 */
export const IssueConflictDataSchema = z.object({ conflicts: z.array(IssueFieldConflictSchema) });
/**
 * 課題を直接作る入力（振り分けを通さない。判定待ちで作る）。
 * @graph-connects none
 */
export const IssueCreateInputSchema = z.object({
    title: IssueTitleSchema,
    what: IssueTextSchema.optional(),
    guidance: IssueTextSchema.optional(),
    fixed: IssueTextSchema.optional(),
});
/**
 * 課題の項目1つの変更。base は画面を開いた時点の値で、今の値と違えば保存しない（項目単位の競合検知）。
 * 中身3項目の文を直すと印は human になる。印だけを human にするのが「確かめた」。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題の編集できる項目（タイトル・中身3項目と確かめた・領域・目標時期・工数の目安・テーマ・関連リンク・メモ）の1項目の変更と、開いた時点の値
 * @graph-connects none
 */
export const IssueFieldChangeSchema = z.discriminatedUnion('field', [
    z.object({ field: z.literal('title'), base: z.string(), value: IssueTitleSchema }),
    z.object({ field: z.literal('whatText'), base: z.string().nullable(), value: IssueTextSchema }),
    z.object({
        field: z.literal('guidanceText'),
        base: z.string().nullable(),
        value: IssueTextSchema,
    }),
    z.object({ field: z.literal('fixedText'), base: z.string().nullable(), value: IssueTextSchema }),
    z.object({
        field: z.literal('whatMark'),
        base: IssueContentMarkSchema.nullable(),
        value: z.literal('human'),
    }),
    z.object({
        field: z.literal('guidanceMark'),
        base: IssueContentMarkSchema.nullable(),
        value: z.literal('human'),
    }),
    z.object({
        field: z.literal('fixedMark'),
        base: IssueContentMarkSchema.nullable(),
        value: z.literal('human'),
    }),
    z.object({
        field: z.literal('domain'),
        base: z.string().nullable(),
        value: IssueDomainSchema.nullable(),
    }),
    z.object({
        field: z.literal('targetMonth'),
        base: z.string().nullable(),
        value: MonthTextSchema.nullable(),
    }),
    z.object({
        field: z.literal('effortEstimate'),
        base: IssueEffortEstimateSchema.nullable(),
        value: IssueEffortEstimateSchema.nullable(),
    }),
    z.object({
        field: z.literal('themeIds'),
        base: z.array(z.string()),
        value: z.array(ThemeIdSchema).max(20),
    }),
    z.object({
        field: z.literal('relatedLinks'),
        base: z.array(z.string()),
        value: z.array(z.url().max(2000)).max(20),
    }),
    z.object({ field: z.literal('memo'), base: z.string().nullable(), value: IssueTextSchema }),
]);
/**
 * 課題の項目を直す入力（1回の保存で複数項目）。
 * @graph-connects none
 */
export const IssueUpdateInputSchema = z.object({
    issueId: IssueIdSchema,
    changes: z.array(IssueFieldChangeSchema).min(1).max(20),
});
/**
 * 判定の入力。base は画面を開いた時点の判定と必須項目の値（変える項目だけ今の値と比べる）。
 * - accepted: 担当・優先度・開発要否が必須
 * - on_hold: 見直す日が必須（翌日〜180日後、日本時間）
 * - rejected: 理由が必須。その他なら補足10字以上
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題の判定（対応する・保留・対応しない）と必須項目、開いた時点の値
 * @graph-connects none
 */
export const IssueDecideInputSchema = z.object({
    issueId: IssueIdSchema,
    decision: IssueDecisionSchema,
    assignee: z.email().optional(),
    priority: IssuePrioritySchema.optional(),
    devRequired: z.boolean().optional(),
    holdUntil: DateTextSchema.optional(),
    rejectReason: IssueRejectReasonSchema.optional(),
    note: z.string().trim().max(500).optional(),
    base: z.object({
        verdict: IssueVerdictSchema,
        assignee: z.string().nullable(),
        priority: IssuePrioritySchema.nullable(),
        devRequired: z.boolean().nullable(),
        holdUntil: z.string().nullable(),
        rejectReason: IssueRejectReasonSchema.nullable(),
        verdictNote: z.string().nullable(),
    }),
});
/**
 * 課題をまとめる入力。revision は画面を開いた時点の両方の課題の版（どちらかが変わっていれば中止する）。
 * @graph-connects none
 */
export const IssueMergeInputSchema = z.object({
    sourceId: IssueIdSchema,
    targetId: IssueIdSchema,
    sourceRevision: z.number().int(),
    targetRevision: z.number().int(),
});
/**
 * 統合を取り消す入力。
 * @graph-connects none
 */
export const IssueUnmergeInputSchema = z.object({
    issueId: IssueIdSchema,
    revision: z.number().int(),
});
/**
 * 課題に案件を紐づける・外す入力。
 * @graph-connects none
 */
export const IssueCaseLinkInputSchema = z.object({
    issueId: IssueIdSchema,
    caseKey: CaseKeySchema,
});
/**
 * 論点を課題に足す入力。issueId か newIssueTitle のどちらか一方。
 * expectedStatus・expectedIssueId は画面を開いた時点の論点の状態（違えば先に振り分けた人を返す）。
 * @graph-connects none
 */
export const PointLinkInputSchema = z
    .object({
    pointId: PointIdSchema,
    issueId: IssueIdSchema.optional(),
    newIssueTitle: IssueTitleSchema.optional(),
    expectedStatus: PointStatusSchema,
    expectedIssueId: IssueIdSchema.nullable(),
})
    .refine((v) => (v.issueId === undefined) !== (v.newIssueTitle === undefined), {
    message: 'issueId か newIssueTitle のどちらか一方を指定してください',
});
/**
 * 論点を課題から外す入力（振り分け前に戻す）。
 * @graph-connects none
 */
export const PointUnlinkInputSchema = z.object({
    pointId: PointIdSchema,
    expectedIssueId: IssueIdSchema,
});
/**
 * Slack 以外の声を手で入れる入力。本文は保存の前に伏せ字を当てる。
 * @graph-connects none
 */
export const ManualVoiceCreateInputSchema = z.object({
    body: z.string().trim().min(1).max(ISSUE_TEXT_MAX_LENGTH),
    source: ManualVoiceSourceSchema,
    postedDate: DateTextSchema,
});
/**
 * テーマを作る入力。
 * @graph-connects none
 */
export const ThemeCreateInputSchema = z.object({
    name: z.string().trim().min(1).max(40),
    description: z.string().trim().max(ISSUE_TEXT_MAX_LENGTH).nullable(),
});
/**
 * テーマを直す入力（名前・説明・有効/終了）。base は開いた時点の値。
 * @graph-connects none
 */
export const ThemeUpdateInputSchema = z.object({
    themeId: ThemeIdSchema,
    name: z.string().trim().min(1).max(40),
    description: z.string().trim().max(ISSUE_TEXT_MAX_LENGTH).nullable(),
    status: ThemeStatusSchema,
    base: z.object({
        name: z.string(),
        description: z.string().nullable(),
        status: ThemeStatusSchema,
    }),
});
/**
 * 元に戻す入力（打ち消す保存操作の ID）。
 * @graph-connects none
 */
export const IssueUndoInputSchema = z.object({ operationId: DocumentIdSchema });
/**
 * 「対応した」を記録する入力（開発しない課題の対応が済んだ）。note は何をしたか（必須）。
 * @graph-connects none
 */
export const IssueMarkDoneInputSchema = z.object({
    issueId: IssueIdSchema,
    note: z.string().trim().min(1).max(ISSUE_TEXT_MAX_LENGTH),
});
/**
 * 「再発した」を記録する入力（解決済みの課題に未解決の印を付けて対応中へ戻す）。
 * @graph-connects none
 */
export const IssueReopenInputSchema = z.object({ issueId: IssueIdSchema });
/**
 * 週次の曜日（判定のカードをまとめて片づける曜日。営業日の月〜金から選ぶ）。
 * @graph-connects none
 */
export const IssueReviewWeekdaySchema = z.enum(['mon', 'tue', 'wed', 'thu', 'fri']);
/**
 * 課題管理の設定（振り分けの当番・判定者・週次の曜日・AI のおすすめを出すか）。
 * 当番と判定者は編集キーを持つ人のメール。まだ誰も決めていなければ null。
 * updatedAt・updatedBy は最後に保存した時刻と人（一度も保存していなければ null）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の設定（振り分けの当番・判定者・週次の曜日・AIのおすすめの表示）
 * @graph-connects none
 */
export const IssueSettingsSchema = z.object({
    triageOwner: z.string().nullable(),
    judge: z.string().nullable(),
    reviewWeekday: IssueReviewWeekdaySchema,
    aiSuggestions: z.boolean(),
    updatedAt: z.string().nullable(),
    updatedBy: z.string().nullable(),
});
/**
 * まだ一度も保存していないときの設定（週次は月曜、AI のおすすめは出す）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の設定を一度も保存していないときの値（当番・判定者は未設定、週次は月曜、AIのおすすめは出す）
 * @graph-connects none
 */
export const ISSUE_SETTINGS_DEFAULTS = { triageOwner: null, judge: null, reviewWeekday: 'mon', aiSuggestions: true };
/**
 * 設定の項目1つの変更。base は画面を開いた時点の値で、今の値と違えば保存しない。
 * 当番と判定者は編集キーを持つ人から選ぶ（空には戻さない）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の設定（当番・判定者・週次の曜日・AIのおすすめ）の1項目の変更と、開いた時点の値
 * @graph-connects none
 */
export const IssueSettingChangeSchema = z.discriminatedUnion('field', [
    z.object({ field: z.literal('triageOwner'), base: z.string().nullable(), value: z.email() }),
    z.object({ field: z.literal('judge'), base: z.string().nullable(), value: z.email() }),
    z.object({
        field: z.literal('reviewWeekday'),
        base: IssueReviewWeekdaySchema,
        value: IssueReviewWeekdaySchema,
    }),
    z.object({ field: z.literal('aiSuggestions'), base: z.boolean(), value: z.boolean() }),
]);
/**
 * 設定を直す入力。
 * @graph-connects none
 */
export const IssueSettingsUpdateInputSchema = z.object({
    changes: z.array(IssueSettingChangeSchema).min(1).max(4),
});
//# sourceMappingURL=issue-mgmt.js.map